import React from 'react';
import { spans } from 'next/dist/build/webpack/plugins/profiling-plugin';
import { useRouter } from 'next/router';
import { ICommonBlockProps } from '@/interfaces/general.interface';
import ActionURL from '@adminComponents/ActionURL/ActionURL';
import InputFormContext from '@adminComponents/Inputs/InputFormContext';
import InputSelectContext from '@adminComponents/Inputs/InputSelectContext';
import TitleBlock from '@adminComponents/TitleSection/TitleBlock';
import Breadcrumbs from '@common/Breadcrumbs';
import SvgChartIcon from '@common/Icons/ChartIcon';
import SvgDocument2 from '@common/Icons/Document2';
import Document2 from '@common/Icons/Document2';
import { DocumentIcon } from '@common/Icons/DocumentIcon';
import SvgEmail from '@common/Icons/Email';
import SvgHelpIcon from '@common/Icons/HelpIcon';
import SvgHome from '@common/Icons/Home';
import SvgMail from '@common/Icons/Mail';
import SvgPhone from '@common/Icons/Phone';
import SvgSettingIcon from '@common/Icons/SettingIcon';
import SvgUser from '@common/Icons/User';
import {
  AccessibilityButtonDetailDocument,
  AccessibilityIconType,
  SocialNetworkType,
  useCreateAccessibilityButtonMutation,
  type SubPages,
} from '@common/Providers/GraphqlProvider/generated';
import { DocumentTextIcon } from '@heroicons/react/20/solid';
import { t } from '@lingui/macro';
import {
  SchemaAccessibilityButtonItem,
  SchemaAccessibilityButtonItemType,
  SchemaTabSocialNetworkType,
} from '@utils/schema/schemas';
import { loadingWrapperPromise } from '@utils/store/actions';
import { useModalDialogStore } from '@utils/store/store';
import { showErrorToast, showSuccessToast } from '@utils/toast/toastHelpers';
import { MdEmail } from 'react-icons/md';

import { AppFormProvider } from '@/components/views/Admin/components/Form';
import SaveCancelButtons from '@/components/views/Admin/components/FormButtons/SaveCancelButtons';

const AccessibilityButtonAdd = ({ defaultBreadcrumb, prevRoute = '/' }: ICommonBlockProps) => {
  const {
    replace,
    query: { id },
  } = useRouter();
  const { showVisible } = useModalDialogStore();
  const [create] = useCreateAccessibilityButtonMutation({
    refetchQueries: [
      {
        query: AccessibilityButtonDetailDocument,
        variables: { pageBlockId: id as string },
      },
    ],
  });

  const breadcrumbItems =
    defaultBreadcrumb?.concat([
      {
        name: 'Botón de accesibilidad',
        route: prevRoute,
      },
      {
        name: t`Nuevo enlace`,
        route: prevRoute,
      },
    ]) || [];

  const handleGoBack = () => {
    if (prevRoute) {
      replace(prevRoute);
    }
  };
  const onSave = (data: SchemaAccessibilityButtonItemType) => {
    showVisible({
      modalVisible: true,
      title: 'Guardar Cambios',
      message: '¿Está seguro que desea guardar los cambios?',
      onConfirm: () => onConfirm(data),
    });
  };

  const onConfirm = (data: SchemaAccessibilityButtonItemType) => {
    showVisible({ modalVisible: false });
    loadingWrapperPromise(
      create({
        variables: {
          input: {
            accessibilityButtonItem: {
              linkName: data.linkName,
              description: data.linkName,
              altText: data.linkName,
              iconType: data.iconType,
              parentId: id as string,
              linkItem: {
                arguments: data.arguments as string[],
                routeNavigation: data.routeNavigation as SubPages,
                routeType: data.routeType,
                target: data.target,
                url: data.url,
              },
            },
          },
        },
        onError: () => {
          showErrorToast('Ocurrió un error al crear el nuevo enlace');
        },
        onCompleted: () => {
          showSuccessToast('Los cambios se guardaron exitosamente');
          handleGoBack();
        },
      })
    );
  };

  if (!id) {
    return null;
  }

  return (
    <div className="bg-white px-10">
      <Breadcrumbs items={breadcrumbItems} color="text-tundora" colorLink="text-dimGray" />

      <TitleBlock
        showBackButton
        title={'Enlace de acceso'}
        backRoute={prevRoute}
        subtitle="Crea una nuevo enlace de acceso rápido para el botón de accesibilidad"
      />

      <AppFormProvider
        schema={SchemaAccessibilityButtonItem}
        submit={(data) => {
          onSave(data);
        }}
      >
        <FormAccessLink />
        <SaveCancelButtons onCancel={handleGoBack} />
      </AppFormProvider>
    </div>
  );
};

export const ACCESSIBILITY_ICON_LIST = [
  {
    label: <SvgUser className={'text-primary'} width={24} height={24} />,
    value: AccessibilityIconType.User,
  },
  {
    label: <SvgChartIcon className={'text-primary'} width={24} height={24} />,
    value: AccessibilityIconType.Chart,
  },
  {
    label: <MdEmail className={'text-primary'} size={24} />,
    value: AccessibilityIconType.Email,
  },
  {
    label: <SvgHelpIcon className={'text-primary'} width={24} height={24} />,
    value: AccessibilityIconType.Help,
  },
  {
    label: <SvgHome className={'text-primary'} width={24} height={24} />,
    value: AccessibilityIconType.Home,
  },
  {
    label: <DocumentTextIcon className={'text-primary'} width={24} height={24} />,
    value: AccessibilityIconType.Document,
  },
  {
    label: <SvgSettingIcon className={'text-primary'} width={24} height={24} />,
    value: AccessibilityIconType.Settings,
  },
  {
    label: <SvgPhone className={'text-primary'} width={24} height={24} />,
    value: AccessibilityIconType.ThumbsUp,
  },
];

export  const FormAccessLink = () => {
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <InputFormContext title={`${'Título'}`} currentId="linkName" />
        <InputSelectContext
          currentId="iconType"
          options={ACCESSIBILITY_ICON_LIST}
          title={'Ícono de enlace'}
        />
      </div>
      <ActionURL />
    </>
  );
};

export default AccessibilityButtonAdd;
